import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const BarChart = ({ data, isHindi, darkMode }) => {
  let categories = [];
  let series = [];

  /**
   * ✅ 0. NEW LINE-CHART JSON SUPPORT (ONLY ADDITION)
   * Structure:
   * data.series -> [{ name, color, data: [{x, y}] }]
   */
  if (data?.series?.length && data?.series[0]?.data?.[0]?.x !== undefined) {
    categories = data.series[0].data.map((point) => point.x);

    series = data.series.map((s) => ({
      name: s.name,
      color: s.color,
      data: s.data.map((point) => point.y),
    }));
  } else {
    /**
     * ✅ 1. MULTI-SERIES (existing explicit config – NO CHANGE)
     */
    const list = data['chart-data'] || [];
    categories = list.map((item) => item.year);

    if (data.seriesConfig && data.seriesConfig.length) {
      series = data.seriesConfig.map((cfg) => ({
        name: cfg.name,
        color: cfg.color,
        yAxis: cfg.yAxis ?? 0,
        data: list.map((item) => item[cfg.key] ?? null),
      }));
    } else if (
      /**
       * ✅ 2. AUTO MULTI-SERIES (China / India / Pakistan – NO CHANGE)
       */
      list.length &&
      Object.keys(list[0]).some((k) => ['china', 'india', 'pakistan'].includes(k))
    ) {
      const autoSeries = [
        { key: 'china', name: isHindi ? 'चीन' : 'China', color: '#EF4444' },
        { key: 'pakistan', name: isHindi ? 'पाकिस्तान' : 'Pakistan', color: '#22C55E' },
        { key: 'india', name: isHindi ? 'भारत' : 'India', color: '#3B82F6' },
      ];

      series = autoSeries.map((cfg) => ({
        name: cfg.name,
        color: cfg.color,
        data: list.map((item) => item[cfg.key] ?? null),
      }));
    } else {
      /**
       * ✅ 3. SINGLE SERIES (Power, GDP, etc. – NO CHANGE)
       */
      series = [
        {
          name: data.label || '',
          color: data?.series?.color,
          data: list.map((item) => item.value),
        },
      ];
    }
  }

  // Dark mode theme configuration
  const chartTheme = darkMode
    ? {
        backgroundColor: '#234046',
        textColor: '#FFFFFF',
        gridLineColor: '#3B3B3B',
        lineColor: '#575757',
      }
    : {
        backgroundColor: '#FFFFFF',
        textColor: '#2b2b2b',
        gridLineColor: '#E6E6E6',
        lineColor: '#CCCCCC',
      };

  const options = {
    chart: {
      type: 'column',
      height: 420,
      backgroundColor: chartTheme.backgroundColor,
    },

    title: {
      text: data?.title || '',
      style: {
        color: chartTheme.textColor,
      },
    },

    xAxis: {
      categories,
      title: {
        text: data?.xAxis?.title || '',
        style: {
          color: chartTheme.textColor,
        },
      },
      labels: {
        style: {
          color: chartTheme.textColor,
        },
      },
      lineColor: chartTheme.lineColor,
      tickColor: chartTheme.lineColor,
    },

    yAxis: {
      min: data?.yAxis?.min ?? 0,
      max: data?.yAxis?.max ?? null,
      title: {
        text: data?.yAxis?.title || '',
        style: {
          color: chartTheme.textColor,
        },
      },
      labels: {
        style: {
          color: chartTheme.textColor,
        },
      },
      gridLineColor: chartTheme.gridLineColor,
    },

    tooltip: {
      shared: series.length > 1,
      valueSuffix: data?.tooltip?.valueSuffix || ` ${data?.unit || ''}`,
      backgroundColor: darkMode ? '#3B3B3B' : '#FFFFFF',
      borderColor: darkMode ? '#575757' : '#CCCCCC',
      style: {
        color: chartTheme.textColor,
      },
    },

    legend: {
      enabled: series.length > 1,
      itemStyle: {
        color: chartTheme.textColor,
      },
      itemHoverStyle: {
        color: darkMode ? '#FFFFFF' : '#000000',
      },
    },

    plotOptions: {
      column: {
        grouping: true,
        borderRadius: 3,
      },
    },

    series,

    credits: {
      enabled: false,
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default BarChart;
